import { useStep } from '@/hooks/useStep';
import { useEffect, useMemo, useState } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { decodeUrl } from '@/utils/encodeUrl';
import { StepPayment } from '../components';
import {
	StepScheduleUser,
	StepProfessionalList,
	TimelineLayout,
	TimelineStepProps,
	StepSuccess,
} from '@/components';
interface StepConfig extends TimelineStepProps {
	visible?: boolean;
}

const CreateUserSession = () => {
	const { setTotalStep, setCurrentStep } = useStep();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [isInitialized, setIsInitialized] = useState(false);
	const fromBenefitFlow = searchParams.get('from') === 'benefit';
	const encodedStepParam = searchParams.get('d');

	const wizardProfessionalId = useMemo(() => {
		if (!encodedStepParam) return null;
		try {
			return decodeUrl<{ p?: string }>(encodedStepParam).p ?? null;
		} catch {
			return null;
		}
	}, [encodedStepParam]);

	const activeStep = useMemo(() => {
		if (!encodedStepParam) return 1;
		try {
			const parsed = parseInt(
				decodeUrl<{ s: string }>(encodedStepParam).s,
				10,
			);
			return Number.isNaN(parsed) ? 1 : parsed;
		} catch {
			return 1;
		}
	}, [encodedStepParam]);

	const stepConfig: Array<StepConfig> = useMemo(
		() => [
			{
				title: 'Profissional',
				step: 1,
				current: activeStep === 1,
				children: (
					<StepProfessionalList
						key={wizardProfessionalId ?? 'professional-list'}
						showBackButton
					/>
				),
				completed: activeStep > 1,
				visible: true,
			},
			{
				title: 'Data/Hora',
				step: 2,
				current: activeStep === 2,
				children: (
					<StepScheduleUser
						key={wizardProfessionalId ?? 'schedule'}
					/>
				),
				completed: activeStep > 2,
				visible: true,
			},
			{
				title: 'Pagamento',
				step: 3,
				current: activeStep === 3,
				children: <StepPayment />,
				completed: activeStep > 3,
				visible: false,
			},
			{
				title: 'Sucesso',
				step: 4,
				current: activeStep === 4,
				children: <StepSuccess />,
				completed: activeStep === 4,
				isLast: true,
				visible: false,
			},
		],
		[activeStep, wizardProfessionalId],
	);

	const visualSteps = useMemo(() => {
		return stepConfig
			.filter((step) => step.visible)
			.map((step, index, arr) => ({
				...step,
				step: index + 1,
				isLast: index === arr.length - 1,
			}));
	}, [stepConfig]);

	const currentStepComponent = useMemo(() => {
		return stepConfig.find((step) => step.current)?.children;
	}, [stepConfig]);

	useEffect(() => {
		setTotalStep(4);
		setCurrentStep(activeStep);
		setIsInitialized(true);
	}, [activeStep, setCurrentStep, setTotalStep]);

	if (!isInitialized) return null;

	return (
		<section className="flex w-full px-2 pb-4 md:px-4">
			<TimelineLayout
				stepList={visualSteps}
				onBack={fromBenefitFlow ? () => navigate('/clubes') : undefined}
				hideBackButton
			>
				{currentStepComponent}
			</TimelineLayout>
		</section>
	);
};

export { CreateUserSession };
